import React, { useCallback, useEffect, useState } from 'react'
import { mergeLeft } from 'ramda'
import { useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'

import { usePlace } from 'commons/usePlace'
import { useAppLogo, useAppName } from 'commons/usePartner'

import { ReactComponent as EmptyMessageIcon } from 'app/components/icons/empty-message-icon.svg'

import EmptyState from 'app/componentV4/EmptyState'
import PanelPages from 'app/componentV4/Panels/Pages'
import DataTable from 'app/componentV4/Table/DataTable'
import PanelFilter from 'app/componentV4/Panels/Filter'
import ButtonFilter from 'app/componentV4/Filters/ButtonFilter'
import ViewMessage from 'app/main/plansAndBilling/usageHistory/V4/ViewMessage/ViewMessage'
import CalendarSelector from 'app/componentV4/Filters/CalendarSelector'

import { filters } from 'app/main/plansAndBilling/constants'
import { ColumnOfData } from 'app/main/plansAndBilling/usageHistory/V4/ColumnOfData'
import useUsageHistory from 'app/main/plansAndBilling/usageHistory/V4/useUsageHistoryV4'

const useStyles = makeStyles((theme) => ({
  table: {
    '&.data-table': {
      [theme.breakpoints.up('md')]: {
        height: 'calc(100vh - 188px)',
      },
    },
  },
}))

export function UsageHistoryV4() {
  const place = usePlace()
  const classes = useStyles()
  const appName = useAppName()
  const { formatMessage } = useIntl()
  const { logo: appLogo } = useAppLogo()
  const listFilters = filters(formatMessage)

  const [isOpen, setIsOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState({})

  const {
    state: {
      params,
      dateRang,
      filter,
      isLoading,
      usageHistory,
      isLoadingAi,
      aiCredit,
      isEmptyUsageHistory,
    },
    handleFilter,
    handleDateFilter,
    handleFetchAiPreview,
    handleFetchUsageHistory,
  } = useUsageHistory()
  const { data, meta } = usageHistory

  const handleOpenDetail = (data) => {
    setSelectedRow(data)
    setIsOpen(true)
  }

  const fetchFirstUsageHistory = useCallback(() => {
    handleFetchUsageHistory(params, true)
  }, [handleFetchUsageHistory, params])

  useEffect(fetchFirstUsageHistory, [])

  return (
    <PanelPages
      isEmptyState={isEmptyUsageHistory}
      emptyState={
        <EmptyState
          icon={<EmptyMessageIcon />}
          title={formatMessage({
            id: 'planAndBilling.usageHistory.empty.title',
          })}
          description={formatMessage({
            id: 'planAndBilling.usageHistory.empty.description',
          })}
          accesses={[]}
        />
      }
      title={formatMessage({
        id: 'navigation.planAndBilling.usageHistory.label',
      })}
    >
      <PanelFilter
        leftContent={
          <>
            <CalendarSelector
              id="usage-log-filterDate"
              name="filterDate"
              values={dateRang}
              className="mr-5 mt-5"
              label={formatMessage({ id: 'text.dates' })}
              onChange={handleDateFilter}
              disabled={isLoading}
            />
            {listFilters.map((list) => (
              <ButtonFilter
                key={list.key}
                id={list.label}
                label={list.label}
                className="my-3 md:my-0 mx-3 bg-primary-200 text-primary text-14"
                onClick={() => handleFilter(list.key)}
                isActive={list.key === filter}
                disabled={isLoading}
              />
            ))}
          </>
        }
      />
      <DataTable
        data={data}
        className={classes.table}
        columns={ColumnOfData(
          formatMessage,
          appLogo,
          place,
          handleFetchAiPreview,
          isLoadingAi,
          aiCredit,
          appName,
        )}
        pagination={meta}
        isLoading={isLoading}
        onFetch={(value) => {
          handleFetchUsageHistory(mergeLeft(value, params))
        }}
        onSelectedRow={(data) => handleOpenDetail(data)}
        activeRow={selectedRow?.id ?? null}
      />
      {isOpen ? (
        <ViewMessage
          isOpen={isOpen}
          id={selectedRow.id}
          onClose={() => {
            setIsOpen(false)
            setSelectedRow(null)
          }}
        />
      ) : null}
    </PanelPages>
  )
}
